import * as Sentry from '@sentry/react';
import type { AppConfig } from '@/types/auth';
import { scrubBreadcrumb, scrubEvent } from '@/lib/error-scrub';

interface EarlyError {
  message?: string;
  error?: unknown;
  reason?: unknown;
}

declare global {
  interface Window {
    __earlyErrors?: EarlyError[];
    __errTrackingReady?: boolean;
  }
}

let enabled = false;

export function initErrorTracking(config: AppConfig): void {
  if (enabled) return;
  const dsn = config.errorTracking?.dsn;
  // Mark ready even when disabled so the index.html buffer stops growing.
  if (!dsn) {
    window.__errTrackingReady = true;
    window.__earlyErrors = [];
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment: config.app.environment,
      release: `valuegraph@${config.app.version}`,
      sendDefaultPii: false,
      beforeSend: (event) => scrubEvent(event),
      beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
    });
    enabled = true;

    for (const early of window.__earlyErrors ?? []) {
      Sentry.captureException(
        early.error ?? early.reason ?? new Error(early.message ?? 'Unknown pre-init error'),
        { tags: { source: 'pre-init' } },
      );
    }
  } catch (err) {
    // Telemetry is best-effort: a failing tracker must never block app boot.
    console.warn('Error tracking init failed', err);
  } finally {
    // Unconditional: even a throwing init must not leave the buffer growing.
    window.__errTrackingReady = true;
    window.__earlyErrors = [];
  }
}

export function captureError(error: unknown, tags?: Record<string, string>): void {
  if (!enabled) return;
  Sentry.captureException(error, tags ? { tags } : undefined);
}

export function setTrackedUser(email: string | null): void {
  if (!enabled) return;
  Sentry.setUser(email ? { email } : null);
}

export { ErrorBoundary } from '@sentry/react';
